import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { ArrowDownTrayIcon, ChevronDownIcon, ArrowPathIcon } from '@heroicons/react/24/outline'
import api from '../lib/api'
import type { PaginatedResponse, Export, ExportFileInfo } from '../types'
import { formatDistanceToNow } from 'date-fns'
import toast from 'react-hot-toast'

export default function ExportsPage() {
  const queryClient = useQueryClient()
  const [openDropdown, setOpenDropdown] = useState<string | null>(null)
  const [showFilesModal, setShowFilesModal] = useState<string | null>(null)
  const [filesList, setFilesList] = useState<ExportFileInfo[]>([])
  
  const retryMutation = useMutation({
    mutationFn: async (exportId: string) => {
      await api.post(`/exports/${exportId}/retry`)
    },
    onSuccess: () => {
      toast.success('Export restarted')
      queryClient.invalidateQueries({ queryKey: ['exports'] })
    },
    onError: () => {
      toast.error('Failed to retry export')
    },
  })
  
  const { data, isLoading } = useQuery({
    queryKey: ['exports'],
    queryFn: async () => {
      const res = await api.get<PaginatedResponse<Export>>('/exports')
      return res.data
    },
    refetchInterval: 5000, // Refresh every 5 seconds to update status
  })
  
  const fetchIndividualFiles = async (exportId: string) => {
    try {
      const res = await api.get<ExportFileInfo[]>(`/exports/${exportId}/files`)
      setFilesList(res.data)
      setShowFilesModal(exportId)
    } catch (error) {
      console.error('Failed to fetch files:', error)
    }
  }
  
  // Helper function to download files through backend proxy
  const downloadFile = async (exportId: string, filename: string) => {
    try {
      const response = await api.get(`/exports/${exportId}/download`, {
        responseType: 'blob',
      })
      
      // Create blob URL and trigger download
      const blob = new Blob([response.data])
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = filename
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)
    } catch (error) {
      console.error('Download failed:', error)
      toast.error('Failed to download file')
    }
  }
  
  // Helper function to download individual files from split exports
  const downloadIndividualFile = async (exportId: string, filePath: string, filename: string) => {
    try {
      const response = await api.get(`/exports/${exportId}/download-file`, {
        params: { file_path: filePath },
        responseType: 'blob',
      })
      
      // Create blob URL and trigger download
      const blob = new Blob([response.data])
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = filename
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)
    } catch (error) {
      console.error('Download failed:', error)
      toast.error('Failed to download file')
    }
  }
  
  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
      </div>
    )
  }
  
  return (
    <div>
      <div className="mb-6">
        <h1 className="text-2xl font-semibold text-gray-900">Exports</h1>
        <p className="mt-1 text-sm text-gray-600">
          Download exported data files
        </p>
      </div>
      
      {data?.items.length ? (
        <div className="card overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Name
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Rows
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Size
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Created
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Download
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {data.items.map((exp) => (
                <tr key={exp.id}>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-2">
                      <div>
                        <p className="text-sm font-medium text-gray-900">{exp.name}</p>
                        {exp.campaign_name && (
                          <p className="text-xs text-gray-500">Campaign: {exp.campaign_name}</p>
                        )}
                      </div>
                      {exp.file_count > 1 && (
                        <span className="inline-flex items-center rounded-full bg-blue-100 px-2 py-0.5 text-xs font-medium text-blue-700">
                          {exp.file_count} files
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${
                      exp.status === 'completed'
                        ? 'bg-green-100 text-green-700'
                        : exp.status === 'processing'
                        ? 'bg-yellow-100 text-yellow-700'
                        : exp.status === 'pending'
                        ? 'bg-gray-100 text-gray-700'
                        : 'bg-red-100 text-red-700'
                    }`}>
                      {exp.status}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-500">
                    {exp.row_count?.toLocaleString() || '—'}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-500">
                    {exp.file_size_bytes
                      ? `${(exp.file_size_bytes / 1024 / 1024).toFixed(2)} MB`
                      : '—'}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-500">
                    {formatDistanceToNow(new Date(exp.created_at), { addSuffix: true })}
                  </td>
                  <td className="px-6 py-4 text-right">
                    {exp.status === 'completed' && (exp.download_url || exp.download_zip_url) ? (
                      exp.file_count > 1 ? (
                        // Split export - show dropdown
                        <div className="relative inline-block text-left">
                          <button
                            onClick={() => setOpenDropdown(openDropdown === exp.id ? null : exp.id)}
                            className="inline-flex items-center gap-1 text-primary-600 hover:text-primary-700"
                          >
                            <ArrowDownTrayIcon className="h-5 w-5" />
                            <ChevronDownIcon className="h-4 w-4" />
                          </button>
                          
                          {openDropdown === exp.id && (
                            <div className="absolute right-0 z-10 mt-2 w-56 rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5">
                              <div className="py-1">
                                <button
                                  onClick={() => {
                                    downloadFile(exp.id, `${exp.name}.zip`)
                                    setOpenDropdown(null)
                                  }}
                                  className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                                >
                                  Download ZIP
                                </button>
                                <button
                                  onClick={() => {
                                    fetchIndividualFiles(exp.id)
                                    setOpenDropdown(null)
                                  }}
                                  className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                                >
                                  Download Individual Files
                                </button>
                              </div>
                            </div>
                          )}
                        </div>
                      ) : (
                        // Single file export
                        <button
                          onClick={() => downloadFile(exp.id, `${exp.name}.csv`)}
                          className="inline-flex items-center text-primary-600 hover:text-primary-700"
                        >
                          <ArrowDownTrayIcon className="h-5 w-5" />
                        </button>
                      )
                    ) : exp.status === 'failed' ? (
                      <button
                        onClick={() => retryMutation.mutate(exp.id)}
                        disabled={retryMutation.isPending}
                        className="inline-flex items-center gap-1 text-primary-600 hover:text-primary-700 disabled:opacity-50"
                        title={exp.error_message || 'Retry export'}
                      >
                        <ArrowPathIcon className="h-5 w-5" />
                        <span className="text-xs">Retry</span>
                      </button>
                    ) : (
                      <span className="text-xs text-gray-400">—</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="card p-12 text-center">
          <p className="text-gray-500 mb-4">No exports yet</p>
          <p className="text-sm text-gray-400">
            Create exports from dataset detail pages
          </p>
        </div>
      )}
      
      {/* Individual files modal */}
      {showFilesModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-screen items-center justify-center p-4">
            <div className="fixed inset-0 bg-black bg-opacity-25" onClick={() => setShowFilesModal(null)} />
            
            <div className="relative bg-white rounded-lg shadow-xl max-w-2xl w-full p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Download Individual Files</h3>
              
              <div className="space-y-2 max-h-96 overflow-y-auto">
                {filesList.map((file, idx) => (
                  <button
                    key={idx}
                    onClick={() => downloadIndividualFile(showFilesModal, file.file_path, file.filename)}
                    className="w-full flex items-center justify-between p-3 border rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    <div>
                      <p className="text-sm font-medium text-gray-900">{file.filename}</p>
                      {file.row_count && (
                        <p className="text-xs text-gray-500">{file.row_count.toLocaleString()} rows</p>
                      )}
                    </div>
                    <ArrowDownTrayIcon className="h-5 w-5 text-primary-600" />
                  </button>
                ))}
              </div>
              
              <div className="mt-6 flex justify-end">
                <button
                  onClick={() => setShowFilesModal(null)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
